import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { apiRequest } from '../helpers/api';

export interface Comment {
  _id: string;
  authorId?: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  postId: string;
  likes?: string[];
}

export interface CreateCommentDto {
  content: string;
  postId: string;
}

export const createNewComment = createAsyncThunk(
  'comments/createNewComment',
  async (newComment: CreateCommentDto) => {
    const result = await apiRequest<Comment>('POST', '/comments', undefined, {
      content: newComment.content,
      postId: newComment.postId,
    });

    return result;
  },
);

export const fetchCommentsByPostId = createAsyncThunk(
  'comments/fetchCommentsByPostId',
  async (postId: string) => {
    const result = await apiRequest<Comment[]>(
      'GET',
      `/posts/${postId}/comments`,
    );

    return result;
  },
);

export const addLikeToComment = createAsyncThunk(
  'comments/addLikeToComment',
  async (commentId: string) => {
    const result = await apiRequest<Comment>(
      'POST',
      `/comments/${commentId}/like`,
    );

    return { id: result._id, changes: { likes: result.likes } };
  },
);

export const removeLikeOfComment = createAsyncThunk(
  'comments/removeLikeOfComment',
  async (commentId: string) => {
    const result = await apiRequest<Comment>(
      'DELETE',
      `/comments/${commentId}/like`,
    );

    return { id: result._id, changes: { likes: result.likes } };
  },
);

export const commentsAdapter = createEntityAdapter<Comment>({
  selectId: (comment: Comment) => comment._id,
});

const commentSlice = createSlice({
  name: 'comments',
  initialState: commentsAdapter.getInitialState(),
  reducers: {
    removeAllComments: (state) => {
      commentsAdapter.removeAll(state);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createNewComment.fulfilled, commentsAdapter.upsertOne);
    builder.addCase(fetchCommentsByPostId.fulfilled, commentsAdapter.setAll);
    builder.addCase(addLikeToComment.fulfilled, commentsAdapter.updateOne);
    builder.addCase(removeLikeOfComment.fulfilled, commentsAdapter.updateOne);
  },
});

export const { removeAllComments } = commentSlice.actions;

export default commentSlice.reducer;
